import { set } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import can from 'ember-can/helpers/can';
import type { Task } from 'ember-concurrency';
import { and } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type LocationModel from 'garaje/models/location';
import type ImpressionsService from 'garaje/services/impressions';
import type MetricsService from 'garaje/services/metrics';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';

interface TouchlessSignInSignature {
  Args: {
    location: LocationModel;
    saveTask: Task<void, [LocationModel]>;
    warningMessage?: string;
    showWarning?: boolean;
    warningHeader?: string;
  };
}

export default class TouchlessSignIn extends Component<TouchlessSignInSignature> {
  @service declare impressions: ImpressionsService;
  @service declare metrics: MetricsService;

  toggleSetting = async (): Promise<void> => {
    const { location, saveTask } = this.args;
    set(location, 'touchlessSigninEnabled', !location.touchlessSigninEnabled);
    await saveTask.perform(location);
    await this._trackSettingToggled(location.touchlessSigninEnabled);
  };

  async _trackSettingToggled(value: boolean): Promise<void> {
    const { location } = this.args;
    const name = 'Pre-registration - Touchless Sign In Setting Toggled';
    this.metrics.trackEvent(name, { toggle_state_after_click: value });
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_TOUCHLESS_SIGN_IN[location.touchlessSigninEnabled ? 'ENABLED' : 'DISABLED'],
    );
  }

  <template>
    <SettingsPanel
      @isOpen={{and @showWarning @location.touchlessSigninEnabled}}
      data-test-touchless-sign-in-panel
      ...attributes
      as |SP|
    >
      <SP.header
        @iconUri="/assets/images/features/touchless-sign-in.svg"
        @title="Touchless sign-in"
        @showCancel={{false}}
        as |Header|
      >
        <Header.description>
          Visitors who have pre-registered can use
          <a
            href="https://apps.apple.com/us/app/envoy-your-workplace-app/id983102576"
            target="_blank"
            rel="noopener"
          >Envoy Mobile for iOS</a>
          and
          <a
            href="https://play.google.com/store/apps/details?id=com.envoy.app"
            target="_blank"
            rel="noopener"
          >Android</a>
          to sign in instead of touching the Visitors kiosk.
        </Header.description>
        <Header.controls>
          <SlideToggle
            data-test-touchless-sign-in-toggle
            @isChecked={{@location.touchlessSigninEnabled}}
            @onClick={{this.toggleSetting}}
            @isDisabled={{@saveTask.isRunning}}
          />
        </Header.controls>
      </SP.header>
      <SP.body as |Content|>
        <Content @style="inset">
          <MessageBox @type="warning" @header={{@warningHeader}} class="mt-4" data-test-touchless-sign-in-warning>
            <p class="mb-0">
              {{@warningMessage}}
              {{#if (can "view visitor-types")}}
                <LinkTo @route="visitors.settings.visitor-types">Manage sign-in flows →</LinkTo>
              {{/if}}
            </p>
          </MessageBox>
        </Content>
      </SP.body>
    </SettingsPanel>
  </template>
}
